import axios from "axios";
import { message } from "antd";

/**
 * ======================================================
 * AXIOS INSTANCE
 * Auth is handled via Bearer token in Authorization header (localStorage).
 * withCredentials is NOT needed and must NOT be set — it forces the browser
 * to require a specific Access-Control-Allow-Origin (not '*'), which breaks
 * the CORS preflight on the live server.
 * ======================================================
 */
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || "https://qrwellness.jyada.in/api",
  timeout: 30000,
  headers: {
    "Content-Type": "application/json",
    "ngrok-skip-browser-warning": "true",
  },
});

/**
 * ======================================================
 * REQUEST INTERCEPTOR (ADD TOKEN)
 * ======================================================
 */
api.interceptors.request.use(
  (config) => {
    // Defensive header (ngrok / proxies)
    config.headers["ngrok-skip-browser-warning"] = "true";

    const token = localStorage.getItem("admin_token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error) => Promise.reject(error),
);

/**
 * ======================================================
 * RESPONSE INTERCEPTOR (GLOBAL ERROR HANDLING)
 * ======================================================
 */
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.config?.skipErrorToast) {
      return Promise.reject(error);
    }
    if (error.response) {
      const { status, data } = error.response;

      switch (status) {
        case 401:
          // Clear expired token
          localStorage.removeItem("admin_token");
          localStorage.removeItem("admin_user");
          delete api.defaults.headers.common["Authorization"];

          // Only redirect if user is on a protected admin page
          const publicRoutes = [
            "/",
            "/about",
            "/features",
            "/contact",
            "/login",
          ];
          const isPublicRoute = publicRoutes.includes(window.location.pathname);

          if (!isPublicRoute) {
            message.error("Session expired. Please login again.");
            window.location.href = "/login";
          }
          break;

        case 403:
          message.error(
            "Access denied. You do not have permission for this action.",
          );
          break;

        case 404:
          message.error("Resource not found.");
          break;

        case 422:
          if (data?.details && Array.isArray(data.details)) {
            const msgs = data.details.map((d) => d.msg).join(", ");
            message.error(`Validation error: ${msgs}`);
          } else {
            message.error(data?.error || "Validation error");
          }
          break;

        case 429:
          message.error("Too many requests. Please try again later.");
          break;

        case 402:
          // Subscription expired - clear auth and redirect to login
          if (data?.code === "SUBSCRIPTION_REQUIRED") {
            localStorage.removeItem("admin_token");
            localStorage.removeItem("admin_user");
            delete api.defaults.headers.common["Authorization"];
            
            // Only redirect if not already on login page
            if (window.location.pathname !== "/login") {
              message.warning("Your subscription has expired. Please complete payment to continue.");
              window.location.href = "/login";
            }
          } else {
            message.error(data?.error || "Payment required.");
          }
          break;

        case 500:
          message.error("Server error. Please try again later.");
          break;

        default:
          message.error(data?.error || "An unexpected error occurred");
      }
    } else if (error.request) {
      message.error(
        "Network error. Please check your connection and try again.",
      );
    } else {
      message.error("An unexpected error occurred");
    }

    return Promise.reject(error);
  },
);

/**
 * ======================================================
 * API MODULES
 * ======================================================
 */

export const authAPI = {
  login: (credentials) => api.post("/auth/admin/login", credentials),

  getWellnessCenters: () => api.get("/auth/wellness-centers"),
};

export const adminAPI = {
  getDashboard: () => api.get("/admin/dashboard"),
  getConsumers: (params) => api.get("/admin/consumers", { params }),
  getConsumer: (id) => api.get(`/admin/consumers/${id}`),
  createConsumer: (data) => api.post("/admin/consumers", data),
  updateConsumer: (id, data) => api.put(`/admin/consumers/${id}`, data),
  deleteConsumer: (id) => api.delete(`/admin/consumers/${id}`),
  getWellnessCenters: () => api.get("/admin/wellness-centers"),
  getCelebrations: () => api.get("/admin/celebrations"),
  getCoaches: () => api.get("/admin/coaches"),
  createCoach: (data) => api.post("/admin/coaches", data),
  sendCelebrationEmails: () => api.post("/admin/celebrations/send"),
  updateProfile: (data) => api.put("/admin/profile", data),
  getGracePeriodStatus: () => api.get("/admin/grace-period-status"),
  getSubscriptionInfo: () => api.get("/admin/subscription-info"),
};

export const attendanceAPI = {
  getAttendance: (params) => api.get("/attendance", { params }),
  checkIn: (data) => api.post("/attendance/check-in", data),
  checkOut: (data) => api.post("/attendance/check-out", data),
  updateAttendance: (id, data) => api.put(`/attendance/${id}`, data),
  deleteAttendance: (id) => api.delete(`/attendance/${id}`),
  getAbsences: (consumerId, params) =>
    api.get(`/attendance/absences/${consumerId}`, { params }),
  getStats: (consumerId) => api.get(`/attendance/stats/${consumerId}`),
  checkMilestones: () => api.post("/attendance/check-milestones"),
  checkAbsences: () => api.post("/attendance/check-absences"),
};

export const measurementsAPI = {
  getMeasurements: (params) => api.get("/measurements", { params }),
  createMeasurement: (data) => api.post("/measurements", data),
  updateMeasurement: (id, data) => api.put(`/measurements/${id}`, data),
  deleteMeasurement: (id) => api.delete(`/measurements/${id}`),
  getConsumerMeasurements: (consumerId, params) =>
    api.get(`/measurements/consumer/${consumerId}`, { params }),
  quickUpdateWeight: (data) => api.post("/measurements/quick-weight", data),
};

export const paymentsAPI = {
  getPayments: (params) => api.get("/payments", { params }),
  createPayment: (data) => api.post("/payments", data),
  updatePayment: (id, data) => api.put(`/payments/${id}`, data),
  deletePayment: (id) => api.delete(`/payments/${id}`),
  getPaymentSummary: (params) => api.get("/payments/summary", { params }),
};

export const biometricAPI = {
  verify: (consumerId, pidData) =>
    api.post("/biometric/verify", {
      consumer_id: consumerId,
      pid_data: pidData,
    }),
  identify: (pidData) => api.post("/biometric/identify", { pid_data: pidData }),
  // 1:N identify: matches via MorFinAuth /verify.
  // Can either capture on server (if backend is local) or accept captured_template from frontend.
  rdIdentify: (data = {}) =>
    api.post("/biometric/rd-identify", data, {
      timeout: 65000,
      skipErrorToast: true,
    }),
  // Proxy: captures fingerprint via backend (avoids CORS preflight to RD Service)
  // Only works if backend is on the same machine as the scanner.
  rdCapture: (pidOptions) =>
    api.post(
      "/biometric/rd-capture",
      { pid_options: pidOptions },
      {
        headers: { "Content-Type": "application/json" },
        timeout: 25000,
      },
    ),
  rdStatus: () => api.get("/biometric/rd-status"),
  rdDebug: () => api.get("/biometric/rd-debug"),
  getAllTemplates: (center_id) =>
    api.get(`/biometric/templates?center_id=${center_id}`),
};

export const centersAPI = {
  getCenters: () => api.get("/centers"),
  getCenter: (id) => api.get(`/centers/${id}`),
  createCenter: (data) => api.post("/centers", data),
  updateCenter: (id, data) => api.put(`/centers/${id}`, data),
  deleteCenter: (id) => api.delete(`/centers/${id}`),
  createCenterAdmin: (centerId, data) =>
    api.post(`/centers/${centerId}/admin`, data),
  getCenterAdmins: (centerId) => api.get(`/centers/${centerId}/admins`),
};

export const contactAPI = {
  submitContactForm: (data) => api.post("/contact", data),
};

export const subscriptionAPI = {
  submitPaymentProof: (data) => {
    const formData = new FormData();
    formData.append('admin_id', data.admin_id);
    formData.append('admin_email', data.admin_email);
    formData.append('upi_transaction_id', data.upi_transaction_id);
    
    // Append file if provided
    if (data.payment_proof_photo) {
      formData.append('payment_proof_photo', data.payment_proof_photo);
    }
    
    return api.post("/subscriptions/payment-proof", formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
  getPaymentRequests: (params) =>
    api.get("/subscriptions/payment-requests", { params }),
  getHistory: () => api.get("/subscriptions/history"),
  approvePaymentRequest: (requestId) =>
    api.post(`/subscriptions/payment-requests/${requestId}/approve`),
  rejectPaymentRequest: (requestId, data) =>
    api.post(`/subscriptions/payment-requests/${requestId}/reject`, data),
  updateSubscription: (subscriptionId, data) =>
    api.put(`/subscriptions/manage/${subscriptionId}`, data),
  calculateAmount: () => api.get("/subscriptions/calculate-amount"),
  getPaymentConfig: () => api.get("/subscriptions/payment-config"),
};

/**
 * ======================================================
 * UTILITIES
 * ======================================================
 */
export const handleApiError = (error, defaultMessage = "Operation failed") => {
  return error.response?.data?.error || defaultMessage;
};

export const buildQueryParams = (filters) => {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      params.append(key, value);
    }
  });
  return params.toString();
};

export default api;
